import * as Sentry from "@sentry/react"
import { State } from "../types/State"
import { replaceStreamTrack } from "../utils/media"

export function isPermissionDeniedError(error: Error): boolean {
  if (error.name === "NotAllowedError") return true

  const message = error.message.toLowerCase()
  return message.includes("permission denied") || message.includes("notallowed")
}

export async function getMedia(
  onError: (error: Error) => void,
  context: State,
): Promise<MediaStream | null> {
  try {
    // Force permission prompt by stopping all tracks
    await navigator.mediaDevices
      .getUserMedia({ audio: true, video: true })
      .then((stream) => stream.getTracks().forEach((track) => track.stop()))
      .catch(() => {})

    // Clear any cached instances
    if (
      "mediaDevices" in navigator &&
      "getUserMedia" in navigator.mediaDevices
    ) {
      const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(
        navigator.mediaDevices,
      )
      // @ts-ignore - Temporarily override getUserMedia to force new permissions
      navigator.mediaDevices.getUserMedia = async function (...args) {
        // Small delay to ensure browser shows permission prompt
        await new Promise((resolve) => setTimeout(resolve, 100))
        return originalGetUserMedia.apply(this, args)
      }
    }

    const devices = await navigator.mediaDevices.enumerateDevices()

    const hasAudioDevice = devices.some(
      (device) => device.kind === "audioinput",
    )
    const hasVideoDevice = devices.some(
      (device) => device.kind === "videoinput",
    )

    if (!hasAudioDevice && !hasVideoDevice) {
      throw new Error("No audio or video devices found")
    }

    const finalConstraints: MediaStreamConstraints = {
      audio: hasAudioDevice
        ? {
            echoCancellation: true,
            noiseSuppression: true,
          }
        : false,
      video: hasVideoDevice ? {} : false,
    }

    // Force a new permission prompt
    const mediaStream =
      await navigator.mediaDevices.getUserMedia(finalConstraints)

    if (context.audioDevice && hasAudioDevice) {
      const audioDevice = devices.find(
        (d) => d.deviceId === context.audioDevice && d.kind === "audioinput",
      )
      if (audioDevice) {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { exact: context.audioDevice },
            echoCancellation: true,
            noiseSuppression: true,
          },
        })
        replaceStreamTrack(
          mediaStream,
          audioStream.getAudioTracks()[0],
          "audio",
        )
      }
    }

    if (context.videoDevice && hasVideoDevice) {
      const videoDevice = devices.find(
        (d) => d.deviceId === context.videoDevice && d.kind === "videoinput",
      )
      if (videoDevice) {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: context.videoDevice },
          },
        })
        replaceStreamTrack(
          mediaStream,
          videoStream.getVideoTracks()[0],
          "video",
        )
      }
    }

    return mediaStream
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error))

    if (isPermissionDeniedError(errorObj)) {
      console.info("Media permissions denied by user:", {
        error: errorObj.name,
        message: errorObj.message,
      })
    } else {
      Sentry.captureException(error)
      console.error("Error accessing media devices:", {
        error,
        audioDevice: context.audioDevice,
        videoDevice: context.videoDevice,
        availableDevices: await navigator.mediaDevices
          .enumerateDevices()
          .then((devices) =>
            devices.map((d) => ({ kind: d.kind, label: d.label })),
          )
          .catch(() => []),
      })
    }

    onError(errorObj)
    return null
  }
}
